import { useState } from "react";
import { CalendarClock, X, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const toLocalInputValue = (d) => {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// Inline picker: the user chooses a time in THEIR local zone; we send UTC.
export const CallbackPicker = ({ inquiryId, onSchedule, onClose, initial }) => {
  const [value, setValue] = useState(toLocalInputValue(initial ? new Date(initial) : new Date(Date.now() + 60 * 60 * 1000)));
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { await onSchedule(inquiryId, new Date(value).toISOString()); onClose(); } finally { setBusy(false); }
  };
  return (
    <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-2" data-testid={`callback-picker-${inquiryId}`}>
      <Input type="datetime-local" value={value} onChange={(e) => setValue(e.target.value)} data-testid={`callback-input-${inquiryId}`}
        className="bg-background border-border rounded-sm h-8 text-xs sm:w-56" />
      <span className="text-[10px] text-muted-foreground">Your local time — shown to the manager in theirs.</span>
      <div className="flex gap-2 sm:ml-auto">
        <Button type="button" size="sm" onClick={submit} disabled={busy || !value} data-testid={`callback-confirm-${inquiryId}`}
          className="rounded-sm h-8 text-xs bg-[#007AFF] hover:bg-[#0066d6] text-white gap-1.5">
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <CalendarClock className="w-3 h-3" />} Schedule
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={onClose} className="rounded-sm h-8 text-xs border-border" data-testid={`callback-picker-cancel-${inquiryId}`}>
          <X className="w-3 h-3" />
        </Button>
      </div>
    </div>
  );
};

export const CallbackBlock = ({ q, onSchedule, onCancel }) => {
  const [picking, setPicking] = useState(false);
  const cb = q.callback;
  const finished = ["called", "call_failed", "callback_scheduled"].includes(q.status) && q.contact_method === "phone";
  if (!finished) return null;
  const scheduled = q.status === "callback_scheduled" && cb;
  return (
    <div className="mt-3" data-testid={`callback-block-${q.id}`}>
      {scheduled && (
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 border border-[#FFCC00]/40 bg-[#FFCC00]/5 px-3 py-2">
          <CalendarClock className="w-3.5 h-3.5 text-[#FFCC00] shrink-0" />
          <div className="min-w-0 text-xs">
            <p className="font-semibold" data-testid={`callback-time-${q.id}`}>
              Callback scheduled · {new Date(cb.at_utc).toLocaleString()} <span className="text-muted-foreground">(your time)</span>
            </p>
            <p className="text-muted-foreground font-mono text-[10px]" data-testid={`callback-local-${q.id}`}>
              {cb.local_label} business time · {cb.source === "agent" ? `arranged on the call · auto-callback ${cb.attempts + 1} of ${cb.max_attempts}` : "set by you"}
              {cb.note ? ` · "${cb.note}"` : ""}
            </p>
          </div>
          <div className="flex gap-2 sm:ml-auto shrink-0">
            <button type="button" onClick={() => setPicking((p) => !p)} data-testid={`callback-reschedule-${q.id}`}
              className="text-[11px] font-bold uppercase tracking-widest text-[#007AFF] hover:underline">Reschedule</button>
            <button type="button" onClick={() => onCancel(q.id)} data-testid={`callback-cancel-${q.id}`}
              className="text-[11px] font-bold uppercase tracking-widest text-[#FF3B30] hover:underline">Cancel</button>
          </div>
        </div>
      )}
      {!scheduled && cb?.notice && (
        <p className="text-xs text-[#FFCC00] border border-[#FFCC00]/30 bg-[#FFCC00]/5 px-3 py-2" data-testid={`callback-notice-${q.id}`}>{cb.notice}</p>
      )}
      {!scheduled && !picking && (
        <button type="button" onClick={() => setPicking(true)} data-testid={`callback-schedule-${q.id}`}
          className="mt-2 text-[11px] font-bold uppercase tracking-widest text-[#007AFF] hover:underline flex items-center gap-1">
          <CalendarClock className="w-3 h-3" /> Schedule callback
        </button>
      )}
      {picking && <CallbackPicker inquiryId={q.id} onSchedule={onSchedule} onClose={() => setPicking(false)} initial={scheduled ? cb.at_utc : null} />}
    </div>
  );
};
